import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  UsersIcon, 
  UserGroupIcon, 
  TrophyIcon,
  UserPlusIcon,
  LinkIcon,
  PhoneIcon,
  CheckIcon,
  HomeIcon,
  CheckBadgeIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { GitBranch } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { formatTrendData } from '../../utils/analytics';
import toast from 'react-hot-toast';
import DashboardLayout from '../ui/DashboardLayout';
import BackToTopButton from '../ui/BackToTopButton';
import StatsCard from '../ui/StatsCard';
import StatsPeriodToggle from '../ui/StatsPeriodToggle';
import Button from '../ui/Button';
import ActionOutcomePanel from '../ui/ActionOutcomePanel';
import Modal from '../ui/Modal';
import PageHeader from '../ui/PageHeader';
import TableToolbar from '../ui/TableToolbar';
import EmptyState from '../ui/EmptyState';
import { FullPageSkeleton } from '../ui/PageSkeleton';
import VendorReferralPromoView from '../enrola/VendorReferralPromoView';
import PendingGiftsView from '../enrola/PendingGiftsView';
import VendorProspectsView from '../enrola/VendorProspectsView';
import VendorPartnersView from '../enrola/VendorPartnersView';
import VendorHomeView from '../enrola/VendorHomeView';
import VideoHub from '../videos/VideoHub';
import ClientClubView from '../enrola/ClientClubView';
import FilterableStateDistribution from '../enrola/FilterableStateDistribution';
import ClientReferralTreeOverlay from '../enrola/ClientReferralTreeOverlay';
import ClientStateTimeline, {
  CLIENT_STATE_ORDER,
  FINAL_CLIENT_STATE,
} from '../enrola/ClientStateTimeline';
import { useProspectConfig } from '../../hooks/useProspectConfig';
import SettingsPanel from './SettingsPanel';
import NotificationsCenter from './NotificationsCenter';
import UserAvatar from '../ui/UserAvatar';
import CopyablePhone from '../ui/CopyablePhone';
import { useNotifications } from '../../hooks/useNotifications';
import { usePendingGiftsMenuBadge } from '../../hooks/usePendingGiftsMenuBadge';
import { navigateFromNotificationAction } from '../../utils/notifications';
import { resolveProspectStateDistribution, computeProspectCompletionRate } from '../../utils/analytics';
import {
  DEFAULT_VENDOR_ANALYTICS_PERIOD,
  normalizeTrendSeries,
} from '../../utils/analytics';
import { vendorStatsPaths } from '../../utils/analytics';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { ELEVATE_HISPANO_SLUG } from '../../utils/elevateEvent';

const CONFETTI_HOLD_MS = 3200;
const CONFETTI_EXIT_EASE = [0.23, 1, 0.32, 1];
const COMPLETION_ENTER = { ease: [0.23, 1, 0.32, 1], duration: 0.4 };

function isIgnorableRequestError(error) {
  if (!error || axios.isCancel(error)) return true;
  const code = error.code;
  if (code === 'ERR_CANCELED' || code === 'ERR_ABORTED') return true;
  if (code === 'ERR_NETWORK' && (error.config?.signal?.aborted || error.message === 'canceled')) {
    return true;
  }
  return false;
}

function pathToView(pathname) {
  const seg = pathname.split('/').filter(Boolean);
  const second = seg[1] || '';
  switch (second) {
    case 'clients':
      return 'clients';
    case 'prospects':
      return 'prospects';
    case 'partners':
      return 'partners';
    case 'analytics':
      return 'analytics';
    case 'settings':
      return 'settings';
    case 'referrals':
      return 'referrals';
    case 'pending-gifts':
      return 'pendingGifts';
    case 'video-hub':
      return 'videoHub';
    case 'club':
      return 'club';
    default:
      return 'dashboard';
  }
}

function VendorDashboard() {
  const { user, logout, checkAuthStatus } = useAuth();
  const { t, formatMessage, locale } = useLanguage();
  const { prospectStatuses } = useProspectConfig();
  const a = t.admin;
  const v = t.vendor;
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState(() => pathToView(location.pathname));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [clientStateFilter, setClientStateFilter] = useState('all');
  const { unreadCount, updateCount } = useNotifications();
  const isElevateHispano = user?.company_slug === ELEVATE_HISPANO_SLUG;
  const { count: pendingGiftsBadgeCount, refreshCount: refreshPendingGiftsBadge } = usePendingGiftsMenuBadge(
    isElevateHispano ? null : 'vendor'
  );
  const [showNotifications, setShowNotifications] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [vendorAnalytics, setVendorAnalytics] = useState(null);
  const [vendorTrends, setVendorTrends] = useState(null);
  const [vendorDetailed, setVendorDetailed] = useState(null);
  const [vendorProspects, setVendorProspects] = useState([]);
  const [analyticsProspectStates, setAnalyticsProspectStates] = useState({});
  const [advancingState, setAdvancingState] = useState(null);
  const [referralTreeClientId, setReferralTreeClientId] = useState(null);
  const [referralTreeData, setReferralTreeData] = useState(null);
  const [referralTreeLoading, setReferralTreeLoading] = useState(false);
  const [referralLinks, setReferralLinks] = useState([]);
  const [pendingGiftsSummary, setPendingGiftsSummary] = useState(null);
  const [homeExtrasLoading, setHomeExtrasLoading] = useState(true);
  const [shortActions, setShortActions] = useState([]);
  const [shortActionsLoading, setShortActionsLoading] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(DEFAULT_VENDOR_ANALYTICS_PERIOD);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsSummaryLoading, setAnalyticsSummaryLoading] = useState(false);
  const [stateAdvancePulse, setStateAdvancePulse] = useState(null);
  const [actionOutcome, setActionOutcome] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [confettiActive, setConfettiActive] = useState(false);
  const confettiDismissTimerRef = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const vendorClientStateData = useMemo(() => {
    if (clients.length > 0) {
      return {
        affiliated: clients.filter((c) => c.state === 'affiliated').length,
        contacted: clients.filter((c) => c.state === 'contacted').length,
        visited: clients.filter((c) => c.state === 'visited').length,
        completed: clients.filter((c) => c.state === 'completed').length,
      };
    }
    if (vendorDetailed?.client_states) {
      return vendorDetailed.client_states;
    }
    return {
      affiliated: 0,
      contacted: 0,
      visited: 0,
      completed: 0,
    };
  }, [vendorDetailed?.client_states, clients]);

  const prospectStateDistribution = useMemo(
    () => resolveProspectStateDistribution(
      analyticsProspectStates || vendorDetailed?.prospect_states,
      vendorProspects
    ),
    [analyticsProspectStates, vendorDetailed?.prospect_states, vendorProspects]
  );

  const handleVendorFetchError = useCallback(async (error, toastMessage) => {
    console.error(toastMessage, error);
    const status = error?.response?.status;
    if (status === 401) {
      await checkAuthStatus();
      return;
    }
    toast.error(toastMessage);
  }, [checkAuthStatus]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get('/api/vendor/clients', {
        withCredentials: true,
      });
      setClients(response.data);
    } catch (error) {
      await handleVendorFetchError(error, v.errorLoadingClients);
    } finally {
      setLoading(false);
    }
  }, [handleVendorFetchError, v.errorLoadingClients]);

  const fetchVendorAnalyticsData = useCallback(async () => {
    try {
      const response = await axios.get(vendorStatsPaths.overview, {
        withCredentials: true,
      });
      setVendorAnalytics(response.data);
    } catch (error) {
      await handleVendorFetchError(error, v.failedLoadAnalytics);
    }
  }, [handleVendorFetchError, v.failedLoadAnalytics]);

  const fetchVendorTrendsData = useCallback(async () => {
    try {
      const response = await axios.get('/api/vendor/trends', {
        withCredentials: true,
      });
      setVendorTrends(response.data);
    } catch (error) {
      await handleVendorFetchError(error, v.failedLoadTrends);
    }
  }, [handleVendorFetchError, v.failedLoadTrends]);

  const fetchVendorDetailedData = useCallback(async () => {
    try {
      const response = await axios.get(vendorStatsPaths.detailed, {
        withCredentials: true,
      });
      setVendorDetailed(response.data);
      if (response.data?.prospect_states) {
        setAnalyticsProspectStates(response.data.prospect_states);
      }
    } catch (error) {
      if (!isIgnorableRequestError(error)) {
        await handleVendorFetchError(error, v.failedLoadAnalytics);
      }
    }
  }, [handleVendorFetchError, v.failedLoadAnalytics]);

  useEffect(() => {
    if (!user?.is_vendor) {
      setLoading(false);
      return undefined;
    }

    fetchClients();
    fetchVendorAnalyticsData();
    fetchVendorTrendsData();
    fetchVendorDetailedData();

    return undefined;
  }, [user?.is_vendor, fetchClients, fetchVendorAnalyticsData, fetchVendorTrendsData, fetchVendorDetailedData]);

  const viewToPath = (view) => {
    switch (view) {
      case 'clients':
        return '/vendor/clients';
      case 'prospects':
        return '/vendor/prospects';
      case 'partners':
        return '/vendor/partners';
      case 'analytics':
        return '/vendor/analytics';
      case 'settings':
        return '/vendor/settings';
      case 'referrals':
        return '/vendor/referrals';
      case 'pendingGifts':
        return '/vendor/pending-gifts';
      case 'videoHub':
        return '/vendor/video-hub';
      case 'club':
        return '/vendor/club';
      default:
        return '/vendor';
    }
  };

  // Sync activeView from URL (pathname only — avoid fighting optimistic setActiveView before navigate)
  useEffect(() => {
    const viewFromPath = pathToView(location.pathname);
    if (location.pathname.endsWith('/notifications')) {
      setShowNotifications(true);
      navigate('/vendor', { replace: true });
      return;
    }
    if (isElevateHispano && viewFromPath === 'pendingGifts') {
      navigate('/vendor', { replace: true });
      setActiveView('dashboard');
      return;
    }
    if (!isElevateHispano && viewFromPath === 'club') {
      navigate('/vendor', { replace: true });
      setActiveView('dashboard');
      return;
    }
    setActiveView(viewFromPath);
  }, [location.pathname, navigate, isElevateHispano]);

  useEffect(() => {
    const onResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    return () => {
      if (confettiDismissTimerRef.current) clearTimeout(confettiDismissTimerRef.current);
    };
  }, []);

  const triggerCompletionConfetti = useCallback(() => {
    if (confettiDismissTimerRef.current) clearTimeout(confettiDismissTimerRef.current);
    setConfettiActive(true);
    confettiDismissTimerRef.current = setTimeout(() => {
      setConfettiActive(false);
    }, CONFETTI_HOLD_MS);
  }, []);

  useEffect(() => {
    if (!user?.is_vendor || activeView !== 'clients') {
      return;
    }
    fetchClients();
  }, [activeView, user?.is_vendor, fetchClients]);

  useEffect(() => {
    if (!user?.is_vendor || activeView !== 'analytics') return undefined;

    const controller = new AbortController();
    const { signal } = controller;

    async function loadAnalyticsViewData() {
      setAnalyticsSummaryLoading(true);
      try {
        const [prospectsResult, statesResult, summaryResult] = await Promise.allSettled([
          axios.get('/api/vendor/prospects', { withCredentials: true, signal }),
          axios.get(vendorStatsPaths.prospectStates, { withCredentials: true, signal }),
          axios.get(vendorStatsPaths.summary, {
            withCredentials: true,
            signal,
            params: { period: analyticsPeriod },
          }),
        ]);

        if (signal.aborted) return;

        if (prospectsResult.status === 'fulfilled') {
          setVendorProspects(prospectsResult.value.data || []);
        } else if (!isIgnorableRequestError(prospectsResult.reason)) {
          console.error('Error loading vendor prospects for analytics:', prospectsResult.reason);
        }

        if (statesResult.status === 'fulfilled') {
          setAnalyticsProspectStates(statesResult.value.data?.prospect_states || {});
        } else if (!isIgnorableRequestError(statesResult.reason)) {
          console.error('Error loading vendor prospect state analytics:', statesResult.reason);
        }

        if (summaryResult.status === 'fulfilled') {
          setAnalyticsSummary(summaryResult.value.data);
        } else if (!isIgnorableRequestError(summaryResult.reason)) {
          console.error('Error loading vendor analytics summary:', summaryResult.reason);
          setAnalyticsSummary(null);
        }
      } finally {
        if (!signal.aborted) {
          setAnalyticsSummaryLoading(false);
        }
      }
    }

    loadAnalyticsViewData();
    return () => controller.abort();
  }, [activeView, analyticsPeriod, user?.is_vendor]);

  useEffect(() => {
    if (!user?.is_vendor || activeView !== 'dashboard') return undefined;

    let cancelled = false;

    async function fetchHomeExtras() {
      setHomeExtrasLoading(true);
      setShortActionsLoading(true);
      try {
        const giftsRequest = isElevateHispano
          ? Promise.resolve({ data: null })
          : axios.get('/api/vendor/pending-gifts', { withCredentials: true });
        const [linksResult, giftsResult, shortActionsResult] = await Promise.allSettled([
          axios.get('/api/referral-links', { withCredentials: true }),
          giftsRequest,
          axios.get('/api/vendor/short-actions', { withCredentials: true }),
        ]);

        if (!cancelled) {
          if (linksResult.status === 'fulfilled') {
            setReferralLinks(linksResult.value.data || []);
          } else {
            console.error('Error loading referral links:', linksResult.reason);
          }

          if (isElevateHispano) {
            setPendingGiftsSummary(null);
          } else if (giftsResult.status === 'fulfilled') {
            setPendingGiftsSummary(giftsResult.value.data);
          } else {
            console.error('Error loading pending gifts:', giftsResult.reason);
          }

          if (shortActionsResult.status === 'fulfilled') {
            setShortActions(shortActionsResult.value.data.actions || []);
          } else {
            console.error('Error loading short actions:', shortActionsResult.reason);
            setShortActions([]);
          }
        }
      } catch (error) {
        console.error('Error loading vendor home data:', error);
      } finally {
        if (!cancelled) {
          setHomeExtrasLoading(false);
          setShortActionsLoading(false);
        }
      }

      try {
        const prospectsResponse = await axios.get('/api/vendor/prospects', { withCredentials: true });
        if (!cancelled) {
          setVendorProspects(prospectsResponse.data || []);
        }
      } catch (error) {
        console.error('Error loading vendor prospects for home:', error);
      }
    }

    fetchHomeExtras();
    return () => {
      cancelled = true;
    };
  }, [activeView, user?.is_vendor, isElevateHispano]);

  const navigateToView = (view, { search = '' } = {}) => {
    const path = viewToPath(view);
    if (search) {
      navigate(`${path}${search}`);
      return;
    }
    navigate({ pathname: path, search: '' });
  };

  const handleOpenProspects = () => {
    navigateToView('prospects');
  };

  const handleQuickCreateProspect = () => {
    navigateToView('prospects', { search: '?create=1' });
  };

  const refreshShortActions = useCallback(async () => {
    try {
      const response = await axios.get('/api/vendor/short-actions', { withCredentials: true });
      setShortActions(response.data.actions || []);
    } catch (error) {
      console.error('Error refreshing short actions:', error);
    }
  }, []);

  const handleNotificationClick = () => {
    setShowNotifications(true);
  };

  const handleNotificationNavigate = useCallback((actionPath) => {
    navigateFromNotificationAction(navigate, actionPath);
  }, [navigate]);

  const getPageTitle = () => {
    switch (activeView) {
      case 'analytics':
        return v.clientAnalytics;
      case 'clients':
        return v.clients;
      case 'prospects':
        return v.prospects.title;
      case 'partners':
        return (t.enrola?.partnerNetworkView?.title) || 'Partner network';
      case 'settings':
        return v.settings;
      case 'referrals':
        return t.enrola?.promoCard || 'Promo & virtual card';
      case 'pendingGifts':
        return t.pendingGifts.title;
      case 'videoHub':
        return t.videoHub.title;
      case 'club':
        return t.enrola.club;
      default:
        return v.dashboardTitle;
    }
  };

  const getPageSubtitle = () => {
    switch (activeView) {
      case 'analytics':
        return v.analyticsSubtitle;
      case 'clients':
        return v.clientsSubtitle;
      case 'prospects':
        return v.prospects.subtitle;
      case 'partners':
        return (t.enrola?.partnerNetworkView?.subtitle) || 'Partners you recruited and pending partner prospects.';
      case 'settings':
        return v.settingsSubtitle;
      case 'referrals':
        return v.promoSubtitle;
      case 'pendingGifts':
        return t.pendingGifts.vendorSubtitle;
      case 'videoHub':
        return t.videoHub.vendorSubtitle;
      case 'club':
        return t.enrola?.clubView?.elevateSubtitle || t.enrola.shoppingClubBtn;
      default:
        return t.enrola?.vendorHome?.homeSubtitle || formatMessage(v.welcomeOverview, { name: user?.username || '' });
    }
  };

  const viewClientDetails = (clientData) => {
    setSelectedClient(clientData);
    setShowClientModal(true);
  };

  const advanceClientState = async (clientId) => {
    setAdvancingState(clientId);
    try {
      const response = await axios.post(`/api/vendor/clients/${clientId}/advance-state`, {}, {
        withCredentials: true
      });

      const newState = response.data.new_state;
      const pointsAwarded = response.data.points_awarded || 0;

      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId
            ? {
                ...client,
                state: newState,
                reward_points: (client.reward_points || 0) + pointsAwarded,
              }
            : client
        )
      );

      if (selectedClient?.id === clientId) {
        setSelectedClient((prev) =>
          prev
            ? {
                ...prev,
                state: newState,
                reward_points: (prev.reward_points || 0) + pointsAwarded,
              }
            : prev
        );
      }

      setStateAdvancePulse({ clientId, state: newState, tick: Date.now() });

      if (newState === FINAL_CLIENT_STATE) {
        triggerCompletionConfetti();
      }
      
      toast.success(
        `${formatMessage(v.clientAdvanced, { state: response.data.new_state })} ${
          pointsAwarded > 0
            ? formatMessage(v.pointsAwarded, { count: pointsAwarded })
            : ''
        }`
      );

      const advancedClient = clients.find((client) => client.id === clientId) || selectedClient;
      const outcomeCopy = t.ux?.actionOutcome?.clientAdvanced;
      const stateLabel = getStateLabel(newState)?.label || newState;
      setActionOutcome({
        id: `advance-${clientId}-${Date.now()}`,
        title: outcomeCopy?.title || formatMessage(v.clientAdvanced, { state: stateLabel }),
        description: advancedClient?.username
          ? `${advancedClient.username} · ${stateLabel}`
          : stateLabel,
        nextStep: outcomeCopy?.nextStep,
      });
      
      fetchClients();
    } catch (error) {
      console.error('Error advancing client state:', error);
      toast.error(error.response?.data?.error || v.failedAdvanceState);
    } finally {
      setAdvancingState(null);
    }
  };

  const getStateLabel = (state) => {
    const cs = v.clientStates || {};
    const labels = {
      affiliated: { label: cs.affiliated || a.affiliated, icon: UserPlusIcon, color: 'bg-orange-100 text-orange-700' },
      contacted: { label: cs.contacted || a.contacted, icon: PhoneIcon, color: 'bg-red-100 text-red-700' },
      visited: { label: cs.visited || a.visited, icon: HomeIcon, color: 'bg-orange-100 text-orange-700' },
      completed: { label: cs.completed || a.completed, icon: CheckBadgeIcon, color: 'bg-success-100 text-success-700' },
    };
    return labels[state] || labels.affiliated;
  };

  const getNextStateLabel = (state) => {
    const currentIndex = CLIENT_STATE_ORDER.indexOf(state);
    if (currentIndex >= CLIENT_STATE_ORDER.length - 1) return null;
    return getStateLabel(CLIENT_STATE_ORDER[currentIndex + 1]);
  };

  const requestAdvanceClientState = async (clientId) => {
    const client =
      clients.find((entry) => entry.id === clientId) ||
      (selectedClient?.id === clientId ? selectedClient : null);
    const nextState = getNextStateLabel(client?.state || 'affiliated');
    if (!nextState) return;

    const ok = await confirm({
      title: formatMessage(v.confirmAdvanceTitle, { name: client?.username || v.clientDetails }),
      message: formatMessage(v.confirmAdvanceMessage, { state: nextState.label }),
      confirmLabel: v.confirmAdvanceConfirm || t.common.confirm,
      confirmVariant: 'primary',
    });
    if (!ok) return;
    await advanceClientState(clientId);
  };

  const getClientAdvanceTick = (clientId) =>
    stateAdvancePulse?.clientId === clientId ? stateAdvancePulse.tick : 0;

  const renderDashboard = () => (
    <VendorHomeView
      username={user?.username}
      clients={clients}
      clientsLoading={loading}
      vendorAnalytics={vendorAnalytics}
      vendorTrends={vendorTrends}
      clientStateData={vendorClientStateData}
      prospectStateData={prospectStateDistribution}
      prospects={vendorProspects}
      prospectStatuses={prospectStatuses}
      referralLinks={referralLinks}
      pendingGifts={pendingGiftsSummary}
      pendingGiftsLoading={homeExtrasLoading}
      hidePendingGifts={isElevateHispano}
      shortActions={shortActions}
      shortActionsLoading={shortActionsLoading}
      onRefreshShortActions={refreshShortActions}
      onCreateProspect={handleQuickCreateProspect}
      onOpenProspects={handleOpenProspects}
      onOpenPendingGifts={() => navigateToView('pendingGifts')}
      onOpenClients={() => navigateToView('clients')}
      onOpenReferrals={() => navigateToView('referrals')}
      onOpenAnalytics={() => navigateToView('analytics')}
      onViewClient={viewClientDetails}
      getStateLabel={getStateLabel}
    />
  );

  const renderContent = () => {
    switch (activeView) {
      case 'clients':
        return renderClientsView();
      case 'prospects':
        return <VendorProspectsView />;
      case 'partners':
        return <VendorPartnersView />;
      case 'analytics':
        return renderAnalyticsView();
      case 'settings':
        return <SettingsPanel />;
      case 'referrals':
        return <VendorReferralPromoView user={user} />;
      case 'pendingGifts':
        return isElevateHispano
          ? renderDashboard()
          : <PendingGiftsView role="vendor" onPendingCountChange={refreshPendingGiftsBadge} />;
      case 'videoHub':
        return <VideoHub audience="vendor" />;
      case 'club':
        return <ClientClubView />;
      default:
        return renderDashboard();
    }
  };

  const openReferralTree = async (client, event) => {
    event.stopPropagation();
    if (referralTreeClientId === client.id) {
      setReferralTreeClientId(null);
      setReferralTreeData(null);
      return;
    }
    setReferralTreeClientId(client.id);
    setReferralTreeData(null);
    setReferralTreeLoading(true);
    try {
      const response = await axios.get(`/api/vendor/clients/${client.id}/referral-tree`, {
        withCredentials: true,
      });
      setReferralTreeData(response.data);
    } catch (error) {
      console.error('Error loading referral tree:', error);
      toast.error(v.couldNotLoadReferralTree);
      setReferralTreeClientId(null);
    } finally {
      setReferralTreeLoading(false);
    }
  };

  const closeReferralTree = () => {
    setReferralTreeClientId(null);
    setReferralTreeData(null);
  };

  const filteredClients = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return clients.filter((client) => {
      const clientState = client.state || 'affiliated';
      if (clientStateFilter !== 'all' && clientState !== clientStateFilter) {
        return false;
      }
      if (!query) return true;

      const username = client.username?.toLowerCase() || '';
      const email = client.email?.toLowerCase() || '';
      const phones = (client.phone_numbers || []).join(' ').toLowerCase();
      return username.includes(query) || email.includes(query) || phones.includes(query);
    });
  }, [clients, searchValue, clientStateFilter]);

  const hasActiveClientFilters =
    searchValue.trim() !== '' || clientStateFilter !== 'all';

  const clearClientFilters = () => {
    setSearchValue('');
    setClientStateFilter('all');
  };

  const renderClientsView = () => (
    <div className="space-y-6">
      <PageHeader
        eyebrow={v.dashboard}
        icon={UserGroupIcon}
        title={v.clients}
        subtitle={v.clientsSubtitle}
      />

      {clients.length > 0 ? (
        <div className="card-premium p-5 xl:p-6 hover:scale-100 max-lg:p-4">
          <TableToolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={v.searchClients}
            filters={[
              {
                id: 'state',
                label: v.filterByState || t.common.table.filterByStatus,
                value: clientStateFilter,
                onChange: setClientStateFilter,
                options: [
                  { value: 'all', label: t.common.table.filterAll },
                  ...CLIENT_STATE_ORDER.map((state) => ({
                    value: state,
                    label: getStateLabel(state).label,
                  })),
                ],
              },
            ]}
            hasActiveFilters={hasActiveClientFilters}
            onClearFilters={clearClientFilters}
            resultCount={filteredClients.length}
            totalCount={clients.length}
          />
        </div>
      ) : null}

      {clients.length === 0 ? (
        <div className="card-premium p-12 text-center">
          <UserGroupIcon className="w-20 h-20 text-secondary-300  mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-secondary-900  mb-2">{v.noClientsAssigned}</h3>
          <p className="text-secondary-600  mb-6">
            {v.noClientsAssignedDesc}
          </p>
          <Button variant="secondary" onClick={() => navigateToView('dashboard')}>
            {v.backToDashboard}
          </Button>
        </div>
      ) : filteredClients.length === 0 ? (
        <EmptyState
          compact
          title={v.noMatchingMembers}
          description={t.common.table.clearFilters}
          className="card-premium mx-0 my-0 p-8"
        />
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredClients
          .map((client) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="card-premium relative overflow-hidden p-6 hover:shadow-glow max-lg:p-4"
            >
              <button
                type="button"
                onClick={(e) => openReferralTree(client, e)}
                title={v.viewReferralTree}
                aria-label={formatMessage(v.viewReferralTreeFor, { name: client.username })}
                aria-expanded={referralTreeClientId === client.id}
                className={`touch-target z-10 flex shrink-0 items-center justify-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold transition-[transform,background-color,border-color,box-shadow] duration-200 max-lg:relative max-lg:top-0 max-lg:right-0 max-lg:mt-3 max-lg:w-full lg:absolute lg:top-3 lg:right-3 lg:h-10 lg:w-10 lg:px-0 lg:py-0 lg:text-[0] ${
                  referralTreeClientId === client.id
                    ? 'border-orange-400 bg-orange-500 text-white shadow-glow'
                    : 'border-secondary-200/80 bg-white/90 text-secondary-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600      '
                }`}
              >
                <GitBranch className="h-5 w-5 shrink-0" />
                <span className="lg:sr-only">{v.viewReferralTree}</span>
              </button>

              <AnimatePresence>
                {referralTreeClientId === client.id && (
                  <ClientReferralTreeOverlay
                    clientUsername={client.username}
                    tree={referralTreeData}
                    loading={referralTreeLoading}
                    onClose={closeReferralTree}
                  />
                )}
              </AnimatePresence>

              <div className="flex items-start space-x-3 sm:space-x-4 mb-4 pr-10">
                <UserAvatar
                  src={client.avatar_url}
                  alt={client.username}
                  size="compact"
                  variant="brand"
                  className="shadow-soft flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-secondary-900  truncate">{client.username}</h4>
                  <p className="text-xs sm:text-sm text-secondary-500  truncate">{client.email}</p>
                  <CopyablePhone
                    value={client.phone_numbers}
                    fallback={a.noPhone}
                    className="text-xs sm:text-sm text-secondary-500  truncate block"
                  />
                </div>
              </div>
              
              {/* State Badge */}
              <div className="mb-3">
                <motion.span
                  key={`${client.id}-${client.state}-${getClientAdvanceTick(client.id)}`}
                  initial={{ opacity: 0.7, scale: 0.92, y: 4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStateLabel(client.state || 'affiliated').color}`}
                >
                  {React.createElement(getStateLabel(client.state || 'affiliated').icon, { className: 'w-3.5 h-3.5 mr-1.5' })}
                  {getStateLabel(client.state || 'affiliated').label}
                </motion.span>
                {client.referred_by_username && (
                  <p className="text-xs text-secondary-500  mt-2">
                    {v.referredBy}: <span className="font-medium">{client.referred_by_username}</span>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600 ">{v.clientReferrals}:</span>
                  <span className="font-semibold text-orange-600">{client.referrals_count || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600 ">{v.rewardPoints}:</span>
                  <span className="font-semibold text-amber-600">{client.reward_points || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600 ">{v.referralCode}:</span>
                  <span className="max-w-[8rem] truncate font-mono text-xs bg-secondary-100 px-2 py-1 rounded sm:max-w-none">{client.referral_code}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-secondary-200">
                <motion.div
                  className="space-y-3"
                  animate={
                    stateAdvancePulse?.clientId === client.id
                      ? { scale: [1, client.state === FINAL_CLIENT_STATE ? 1.02 : 1.012, 1] }
                      : { scale: 1 }
                  }
                  transition={{ duration: 0.55, ease: [0.23, 1, 0.32, 1] }}
                >
                {/* State Timeline */}
                <div className="px-2">
                  <ClientStateTimeline
                    currentState={client.state}
                    variant="compact"
                    getStateLabel={getStateLabel}
                    advanceTick={getClientAdvanceTick(client.id)}
                  />
                </div>

                <AnimatePresence mode="wait">
                  {getNextStateLabel(client.state || 'affiliated') ? (
                    <motion.div
                      key="advance-action"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={COMPLETION_ENTER}
                    >
                      <Button 
                        variant="primary" 
                        size="small" 
                        className="w-full"
                        onClick={() => requestAdvanceClientState(client.id)}
                        disabled={advancingState === client.id}
                      >
                        {advancingState === client.id
                          ? v.advancing
                          : formatMessage(v.advanceTo, { state: getNextStateLabel(client.state || 'affiliated').label })}
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="completed-action"
                      initial={{ opacity: 0, scale: 0.9, y: 12 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: -6 }}
                      transition={{ type: 'spring', stiffness: 360, damping: 24 }}
                      className="text-center py-2 text-success-600 font-medium text-sm flex items-center justify-center rounded-xl border border-success-200/80 bg-success-50/80  "
                    >
                      <motion.span
                        initial={{ rotate: -20, scale: 0.5 }}
                        animate={{ rotate: 0, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 420, damping: 20 }}
                        className="mr-1 inline-flex"
                      >
                        <CheckIcon className="w-5 h-5" />
                      </motion.span>
                      {v.saleCompleted}
                    </motion.div>
                  )}
                </AnimatePresence>
                <Button 
                  variant="ghost" 
                  size="small" 
                  className="w-full"
                  onClick={() => viewClientDetails(client)}
                >
                  {a.viewDetails}
                </Button>
                </motion.div>
              </div>
            </motion.div>
          ))}
      </div>
      )}
    </div>
  );

  const renderAnalyticsView = () => {
    const prospectCompletionRate = computeProspectCompletionRate(
      prospectStateDistribution,
      prospectStatuses
    );
    const stateDistribution = vendorDetailed?.client_states ?? {
      affiliated: clients.filter((c) => c.state === 'affiliated').length,
      contacted: clients.filter((c) => c.state === 'contacted').length,
      visited: clients.filter((c) => c.state === 'visited').length,
      completed: clients.filter((c) => c.state === 'completed').length,
    };
    const sd = t.common.stateDistribution;
    const sc = t.statsCard;
    const summary = analyticsSummary;
    const trendPeriodText = {
      weekly: sc.trendWeekly,
      monthly: sc.trendMonthly,
      yearly: sc.trendYearly,
      all: sc.trendAll,
    }[analyticsPeriod] || sc.trendMonthly;

    const totalClients = summary?.clients ?? clients.length;
    const totalClientReferrals = summary?.referrals
      ?? clients.reduce((sum, c) => sum + (c.referrals_count || 0), 0);
    const totalProspects = summary?.prospects ?? vendorProspects.length;
    const totalLinkConversions = summary?.link_conversions
      ?? vendorAnalytics?.totalVendorConversions
      ?? 0;
    const totalLinkClicks = summary?.link_clicks
      ?? vendorAnalytics?.totalVendorClicks
      ?? 0;
    const linkConversionRate = summary?.link_conversion_rate
      ?? vendorAnalytics?.vendorConversionRate
      ?? (totalLinkClicks > 0 ? (totalLinkConversions / totalLinkClicks) * 100 : 0);

    // Top performing clients
    const topClients = [...clients]
      .filter(c => (c.referrals_count || 0) > 0)
      .sort((a, b) => (b.referrals_count || 0) - (a.referrals_count || 0))
      .slice(0, 5);

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-secondary-600 ">
            {v.analyticsPeriodHint}
          </p>
          <StatsPeriodToggle value={analyticsPeriod} onChange={setAnalyticsPeriod} />
        </div>

        {/* Overview Stats */}
        <div className={`grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4 xl:gap-6 ${analyticsSummaryLoading ? 'opacity-60 pointer-events-none' : ''}`}>
          <StatsCard
            title={v.totalClients}
            value={totalClients}
            change={summary?.change?.clients?.change ?? formatTrendData(vendorTrends, 'clients')?.change ?? 0}
            changeType={summary?.change?.clients?.changeType || formatTrendData(vendorTrends, 'clients')?.changeType || 'neutral'}
            icon={UsersIcon}
            gradient="from-orange-500 to-amber-500"
            description={t.vendor.assignedClients}
            trend={normalizeTrendSeries(summary?.trend?.clients ?? formatTrendData(vendorTrends, 'clients')?.data)}
            periodText={sc.vsLastPeriod}
            trendPeriodText={trendPeriodText}
            animated
          />
          
          <StatsCard
            title={v.linkMemberConversions}
            value={`${totalLinkConversions}/${totalLinkClicks}`}
            change={summary?.change?.link_conversions?.change ?? 0}
            changeType={summary?.change?.link_conversions?.changeType || 'neutral'}
            icon={CheckBadgeIcon}
            gradient="from-green-500 to-emerald-500"
            description={v.linkMemberConversionsDesc}
            trend={normalizeTrendSeries(summary?.trend?.link_conversions)}
            periodText={formatMessage(v.linkConversionRateLabel, {
              percent: Number(linkConversionRate).toFixed(1),
            })}
            trendPeriodText={trendPeriodText}
            animated
          />
          
          <StatsCard
            title={v.clientReferrals}
            value={totalClientReferrals}
            change={summary?.change?.referrals?.change ?? 0}
            changeType={summary?.change?.referrals?.changeType || 'neutral'}
            icon={LinkIcon}
            gradient="from-red-500 to-pink-500"
            description={t.vendor.totalReferrals}
            trend={normalizeTrendSeries(summary?.trend?.referrals)}
            periodText={sc.vsLastPeriod}
            trendPeriodText={trendPeriodText}
            animated
          />
          
          <StatsCard
            title={v.totalProspects}
            value={totalProspects}
            change={summary?.change?.prospects?.change ?? 0}
            changeType={summary?.change?.prospects?.changeType || 'neutral'}
            icon={RocketLaunchIcon}
            gradient="from-violet-500 to-purple-500"
            description={v.prospectsInPipeline}
            trend={normalizeTrendSeries(summary?.trend?.prospects)}
            periodText={formatMessage(v.percentConverted, {
              percent: prospectCompletionRate.toFixed(1),
            })}
            trendPeriodText={trendPeriodText}
            animated
          />
        </div>

        <div className="space-y-6">
          <FilterableStateDistribution
            variant="client"
            title={v.clientStateDistribution}
            clients={clients}
            fallbackStateData={stateDistribution}
            labels={{
              affiliated: v.clientStates?.affiliated || a.affiliated,
              contacted: v.clientStates?.contacted || a.contacted,
              visited: v.clientStates?.visited || a.visited,
              completed: v.clientStates?.completed || a.completed,
            }}
            filterLabels={sd}
            footer={
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-600 ">{v.completionRate}</span>
                <span className="text-lg font-bold text-success-600">
                  {vendorDetailed?.performance_metrics?.completion_rate?.toFixed(1) || '0.0'}%
                </span>
              </div>
            }
          />

          <FilterableStateDistribution
            variant="prospect"
            title={v.prospectStateDistribution}
            prospects={vendorProspects}
            fallbackStateData={prospectStateDistribution}
            prospectStatuses={prospectStatuses}
            t={t}
            filterLabels={sd}
            delay={0.05}
          />
        </div>

        {/* Top Performing Clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-premium p-5 xl:p-6"
        >
          <h3 className="text-lg font-semibold text-secondary-900  mb-4 flex items-center">
            <TrophyIcon className="w-5 h-5 text-amber-500 mr-2" />
            {v.topPerformingClients}
          </h3>
          
          {topClients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {topClients.map((client, index) => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="relative p-4 bg-gradient-to-br from-secondary-50 to-white rounded-xl border-2 border-secondary-200 hover:border-orange-300 hover:shadow-lg transition-[border-color,box-shadow] duration-300    "
                >
                  {/* Ranking Badge */}
                  <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                    index === 0 ? 'bg-amber-500' :
                    index === 1 ? 'bg-secondary-400' :
                    index === 2 ? 'bg-orange-600' :
                    'bg-secondary-300'
                  }`}>
                    {index + 1}
                  </div>

                  {/* Client Info */}
                  <div className="flex flex-col items-center text-center">
                    <UserAvatar
                      src={client.avatar_url}
                      alt={client.username}
                      size="round"
                      variant="brand"
                      className="mb-3 shadow-lg"
                    />
                    
                    <h4 className="font-semibold text-secondary-900  mb-1 truncate w-full">
                      {client.username}
                    </h4>
                    
                    <p className="text-xs text-secondary-500  mb-3 truncate w-full">
                      {client.email}
                    </p>

                    {/* Stats */}
                    <div className="w-full space-y-2">
                      <div className="flex items-center justify-between px-3 py-1 bg-orange-50 rounded-2xl">
                        <span className="text-xs text-secondary-600 ">{v.clientReferrals}</span>
                        <span className="text-sm font-bold text-orange-600">{client.referrals_count}</span>
                      </div>
                      
                      <div className="flex items-center justify-between px-3 py-1 bg-success-50 rounded-2xl">
                        <span className="text-xs text-secondary-600 ">{v.commission}</span>
                        <span className="text-sm font-bold text-success-600">${(client.referrals_count * 25)}</span>
                      </div>

                      {/* State Badge */}
                      <div className="pt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStateLabel(client.state || 'affiliated').color}`}>
                          {React.createElement(getStateLabel(client.state || 'affiliated').icon, { className: 'w-3 h-3 mr-1.5' })}
                          {getStateLabel(client.state || 'affiliated').label}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrophyIcon className="w-16 h-16 text-secondary-300  mx-auto mb-4" />
              <h4 className="text-lg font-medium text-secondary-900  mb-2">{v.noActiveClientsYet}</h4>
              <p className="text-secondary-500 ">{v.noActiveClientsYetDesc}</p>
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  if (loading) {
    return <FullPageSkeleton />;
  }

  const totalReferrals =
    vendorAnalytics?.totalReferrals ??
    clients.reduce((sum, client) => sum + (client.referrals_count || 0), 0);
  const vendorHubStats = [
    {
      id: 'clients',
      label: t.enrola?.vendorHome?.statClients || 'Clients',
      value: vendorAnalytics?.totalClients ?? clients.length,
    },
    {
      id: 'referrals',
      label: t.enrola?.vendorHome?.statReferrals || 'Referrals',
      value: totalReferrals,
      highlight: true,
    },
  ];

  return (
    <DashboardLayout
      activeView={activeView}
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      sidebarProps={{
        activeView,
        setActiveView,
        onLogout: logout,
        notificationsCount: unreadCount,
        userType: 'vendor',
        menuBadges: { pendingGifts: isElevateHispano ? 0 : pendingGiftsBadgeCount },
      }}
      topBarProps={{
        title: getPageTitle(),
        subtitle: getPageSubtitle(),
        onNotificationClick: handleNotificationClick,
        notificationsCount: unreadCount,
        searchValue,
        onSearchChange: setSearchValue,
        onSettingsClick: () => navigateToView('settings'),
        hubStats: vendorHubStats,
      }}
      overlays={
        <>
      {confirmDialog}
      <AnimatePresence>
        {confettiActive ? (
          <motion.div
            key="member-completion-confetti"
            className="pointer-events-none fixed inset-0 z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.4, ease: CONFETTI_EXIT_EASE } }}
            exit={{ opacity: 0, transition: { duration: 2.6, ease: CONFETTI_EXIT_EASE } }}
          >
            <Confetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={280}
              gravity={0.2}
              tweenDuration={7500}
              colors={['#22c55e', '#f97316', '#fbbf24', '#ffffff', '#86efac']}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
      {/* Client Details Modal */}
      <Modal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        title={v.clientDetails}
        size="large"
      >
        {selectedClient && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:space-x-4">
              <UserAvatar
                src={selectedClient.avatar_url}
                alt={selectedClient.username}
                size="lg"
                variant="brand"
                className="shadow-glow shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-xl font-bold text-secondary-900 ">{selectedClient.username}</h3>
                <p className="text-secondary-600 ">{selectedClient.email}</p>
                <CopyablePhone
                  value={selectedClient.phone_numbers}
                  fallback="No phone"
                  className="text-secondary-600 "
                />
              </div>
              <div className="sm:shrink-0">
                <motion.span
                  key={`${selectedClient.id}-${selectedClient.state}-${getClientAdvanceTick(selectedClient.id)}`}
                  initial={{ opacity: 0.7, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStateLabel(selectedClient.state || 'affiliated').color}`}
                >
                  {React.createElement(getStateLabel(selectedClient.state || 'affiliated').icon, { className: 'w-4 h-4 mr-2' })}
                  {getStateLabel(selectedClient.state || 'affiliated').label}
                </motion.span>
              </div>
            </div>
            
            {selectedClient.referred_by_username && (
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3">
                <p className="text-sm text-secondary-700 ">
                  <span className="font-medium">{v.referredBy}:</span> {selectedClient.referred_by_username}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {v.totalReferrals}
                </label>
                <p className="text-2xl font-bold text-orange-600">
                  {selectedClient.referrals_count || 0}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {v.rewardPoints}
                </label>
                <p className="text-2xl font-bold text-amber-600">
                  {selectedClient.reward_points || 0}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {v.clientSince}
                </label>
                <p className="text-sm text-secondary-900 ">
                  {new Date(selectedClient.created_at).toLocaleDateString(locale)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {v.currentState}
                </label>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStateLabel(selectedClient.state || 'affiliated').color}`}>
                  {getStateLabel(selectedClient.state || 'affiliated').label}
                </span>
              </div>
              
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {v.referralCode}
                </label>
                <p className="text-sm text-secondary-900  font-mono bg-secondary-100 px-3 py-2 rounded">
                  {selectedClient.referral_code}
                </p>
              </div>
            </div>
            
            <div className="pt-4 border-t border-secondary-200 space-y-4">
              {/* State Timeline */}
              <div className="bg-secondary-50 rounded-2xl p-4">
                <h4 className="text-sm font-semibold text-secondary-700  mb-4">{v.clientJourneyProgress}</h4>
                <ClientStateTimeline
                  currentState={selectedClient.state}
                  variant="detailed"
                  getStateLabel={getStateLabel}
                  advanceTick={getClientAdvanceTick(selectedClient.id)}
                />
              </div>

              <AnimatePresence mode="wait">
                {getNextStateLabel(selectedClient.state || 'affiliated') ? (
                  <motion.div
                    key="modal-advance-action"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={COMPLETION_ENTER}
                  >
                    <Button 
                      variant="primary" 
                      size="default" 
                      className="w-full"
                      onClick={() => {
                        requestAdvanceClientState(selectedClient.id);
                      }}
                      disabled={advancingState === selectedClient.id}
                    >
                      {advancingState === selectedClient.id
                        ? v.advancing
                        : formatMessage(v.advanceClientTo, {
                            state: getNextStateLabel(selectedClient.state || 'affiliated').label,
                          })}
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="modal-completed-action"
                    initial={{ opacity: 0, scale: 0.92, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: -6 }}
                    transition={{ type: 'spring', stiffness: 360, damping: 24 }}
                    className="text-center py-3 bg-success-50 border border-success-200 rounded-2xl text-success-700 font-medium flex items-center justify-center   "
                  >
                    <motion.span
                      initial={{ rotate: -20, scale: 0.5 }}
                      animate={{ rotate: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 420, damping: 20 }}
                      className="mr-2 inline-flex"
                    >
                      <CheckIcon className="w-6 h-6" />
                    </motion.span>
                    {v.saleCompletedFinal}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </Modal>

      <NotificationsCenter
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        onNotificationUpdate={updateCount}
        onNavigate={handleNotificationNavigate}
      />
      <BackToTopButton />
        </>
      }
    >
      {renderContent()}
      <ActionOutcomePanel
        outcome={actionOutcome}
        onDismiss={() => setActionOutcome(null)}
        className="fixed bottom-4 left-4 right-4 z-[120] mx-auto max-w-xl max-lg:safe-inset-bottom lg:left-auto lg:right-6 lg:max-w-md"
      />
    </DashboardLayout>
  );
}

export default VendorDashboard;
